import React from 'react';
import SmsConsentLabelText from '../elements/SmsConsentLabelText';
import TurnstileWidget from '../elements/TurnstileWidget';
import { trackGenerateLead } from '../../utils/analytics';
import { captureAttribution, createFormSubmissionId } from '../../utils/attribution';
import { getContactEmail } from '../../utils/contact';
import { INTEREST_OPTIONS, getValueBandsForInterest, submitContact } from '../../utils/submitContact';

const TURNSTILE_SITE_KEY =
 process.env.REACT_APP_TURNSTILE_SITE_KEY || '0x4AAAAAAEeBY3g8qvnKqcYm';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const FIELD_LABELS = {
 name: 'Name',
 email: 'Work email',
 organization: 'Organization',
 interest: 'Interested in',
 valueBandId: 'Engagement size'
};

const EMPTY_FIELDS = {
 name: '',
 email: '',
 organization: '',
 phone: '',
 smsConsent: false,
 interest: '',
 valueBandId: '',
 message: '',
 fax_number_hp: ''
};

function summarize(message) {
 const text = message.replace(/[.,]?\s*like .*$/, '').replace(/\.$/, '');
 return text.charAt(0).toLowerCase() + text.slice(1);
}

function validate(fields) {
 const errors = {};
 if (!fields.name.trim()) errors.name = 'Enter your name.';
 if (!fields.email.trim()) {
 errors.email = 'Enter your work email.';
 } else if (!EMAIL_PATTERN.test(fields.email.trim())) {
 errors.email = 'Enter a valid email address, like name@organization.org.';
 }
 if (!fields.organization.trim()) errors.organization = 'Enter your organization.';
 if (!fields.interest) errors.interest = 'Choose what you’re interested in.';
 if (fields.interest && !fields.valueBandId) errors.valueBandId = 'Choose an engagement size.';
 return errors;
}

class LandingForm extends React.Component {
 state = {
 ...EMPTY_FIELDS,
 errors: {},
 turnstileToken: '',
 turnstileReset: 0,
 status: 'idle', // idle | submitting | success | error
 errorMessage: ''
 };

 formOpenedAt = Date.now();
 // Set synchronously so a second submit can't slip in before the busy state renders.
 inFlight = false;
 successHeading = React.createRef();
 errorSummary = React.createRef();

 componentDidUpdate(prevProps, prevState) {
 if (this.state.status === 'success' && prevState.status !== 'success' && this.successHeading.current) {
 this.successHeading.current.focus();
 }
 }

 handleChange = (event) => {
 const { name, value, type, checked } = event.target;
 const next = { [name]: type === 'checkbox' ? checked : value };
 if (name === 'interest') next.valueBandId = '';
 if (name === 'phone' && !value.trim()) next.smsConsent = false;
 this.setState((prev) => {
 if (!prev.errors[name]) return next;
 const errors = { ...prev.errors };
 delete errors[name];
 return { ...next, errors };
 });
 };

 handleTurnstileError = () => {
 // The token for an in-flight request is already spent; let that request settle the form state.
 if (this.inFlight) {
 this.setState({ turnstileToken: '' });
 return;
 }
 this.setState({
 turnstileToken: '',
 status: 'error',
 errorMessage: 'Verification failed to load. Please refresh and try again.'
 });
 };

 handleSubmit = async (event) => {
 event.preventDefault();
 if (this.inFlight) return;
 const errors = validate(this.state);
 if (Object.keys(errors).length) {
 this.setState({ errors, status: 'idle', errorMessage: '' }, () => {
 if (this.errorSummary.current) this.errorSummary.current.focus();
 });
 return;
 }
 if (!this.state.turnstileToken) {
 this.setState({ errors: {}, status: 'error', errorMessage: 'Please complete the verification challenge.' });
 return;
 }

 const { fitSignals } = this.props;
 const fitLine = fitSignals.length ? `From fit check: ${fitSignals.join('; ')}` : '';
 const message = [fitLine, this.state.message.trim()].filter(Boolean).join('\n\n');
 const phone = this.state.phone.trim();

 this.inFlight = true;
 this.setState({ errors: {}, status: 'submitting', errorMessage: '' });
 try {
 await submitContact({
 name: this.state.name.trim(),
 email: this.state.email.trim(),
 organization: this.state.organization.trim(),
 phone,
 smsConsent: Boolean(phone) && this.state.smsConsent,
 interest: this.state.interest,
 valueBandId: this.state.valueBandId,
 message,
 company_url: this.state.fax_number_hp,
 turnstileToken: this.state.turnstileToken,
 formOpenedAt: this.formOpenedAt,
 formSubmissionId: createFormSubmissionId(),
 formName: 'Landing - Technology Strategy Review',
 ...captureAttribution()
 });
 trackGenerateLead(this.state.interest, 'form');
 this.formOpenedAt = Date.now();
 const firstName = this.state.name.trim().split(/\s+/)[0];
 this.setState({ ...EMPTY_FIELDS, status: 'success', turnstileToken: '', firstName });
 } catch (err) {
 this.setState((prev) => ({
 status: 'error',
 errorMessage: err.message || 'Unable to send your message right now.',
 turnstileToken: '',
 turnstileReset: prev.turnstileReset + 1
 }));
 } finally {
 this.inFlight = false;
 }
 };

 fieldProps(name) {
 const error = this.state.errors[name];
 return {
 id: `lp-${name}`,
 name,
 value: this.state[name],
 onChange: this.handleChange,
 disabled: this.state.status === 'submitting',
 'aria-invalid': error ? true : undefined,
 'aria-describedby': error ? `lp-${name}-error` : undefined
 };
 }

 renderError(name) {
 const error = this.state.errors[name];
 return error ? (
 <span className="lp-field-error" id={`lp-${name}-error`}>{error}</span>
 ) : null;
 }

 render() {
 const { fitSignals } = this.props;
 const { status, errorMessage, errors, interest, phone } = this.state;
 const busy = status === 'submitting';
 const valueBands = getValueBandsForInterest(interest);
 const errorKeys = Object.keys(errors);

 if (status === 'success') {
 const { firstName } = this.state;
 return (
 <div className="lp-form-card lp-form-success" role="status">
 <span className="lp-form-success-icon" aria-hidden="true">✓</span>
 <h3 ref={this.successHeading} tabIndex={-1}>Request received.</h3>
 <p>
 {firstName ? `Thanks, ${firstName}. ` : 'Thanks. '}
 An advisor will reply within one business day to schedule your Technology Strategy Review.
 </p>
 <div className="lp-form-wait">
 <p>While you wait</p>
 <p>Jot down upcoming renewals and your current provider list. That’s all the prep we need.</p>
 </div>
 </div>
 );
 }

 return (
 <form
 className="lp-form-card"
 onSubmit={this.handleSubmit}
 noValidate
 aria-labelledby="lp-form-title"
 >
 <h3 id="lp-form-title">Book a Technology Strategy Review</h3>
 <p className="lp-form-sub">We reply within one business day. No system access needed to start.</p>

 {fitSignals.length > 0 && (
 <div className="lp-form-fit">
 <p>From your fit check</p>
 <ul>
 {fitSignals.map((chip) => <li key={chip}>{chip}</li>)}
 </ul>
 </div>
 )}

 {errorKeys.length > 0 && (
 <div className="lp-form-summary" role="alert" tabIndex={-1} ref={this.errorSummary}>
 <p>{errorKeys.length === 1 ? '1 field needs attention' : `${errorKeys.length} fields need attention`}</p>
 <ul>
 {errorKeys.map((key) => (
 <li key={key}>
 <a href={`#lp-${key}`}>{FIELD_LABELS[key]}</a>: {summarize(errors[key])}
 </li>
 ))}
 </ul>
 </div>
 )}

 {status === 'error' && errorMessage && (
 <div className="lp-form-banner" role="alert">
 <span className="lp-form-banner-icon" aria-hidden="true">!</span>
 <div>
 <p>We couldn’t send your request.</p>
 <p>
 {errorMessage} Your answers are saved. Try again, or email{' '}
 <a href={`mailto:${getContactEmail()}`}>{getContactEmail()}</a> and we’ll pick it up from there.
 </p>
 </div>
 </div>
 )}

 <div className="lp-form-grid">
 <label className="lp-field" htmlFor="lp-name">
 <span>Name</span>
 <input {...this.fieldProps('name')} autoComplete="name" required />
 {this.renderError('name')}
 </label>
 <label className="lp-field" htmlFor="lp-email">
 <span>Work email</span>
 <input {...this.fieldProps('email')} type="email" autoComplete="email" required />
 {this.renderError('email')}
 </label>
 <label className="lp-field" htmlFor="lp-organization">
 <span>Organization</span>
 <input {...this.fieldProps('organization')} autoComplete="organization" required />
 {this.renderError('organization')}
 </label>
 <label className="lp-field" htmlFor="lp-phone">
 <span>Phone (optional)</span>
 <input {...this.fieldProps('phone')} type="tel" autoComplete="tel" placeholder="Work or mobile" />
 </label>

 <label className="lp-consent lp-span-2">
 <input
 type="checkbox"
 name="smsConsent"
 checked={this.state.smsConsent}
 onChange={this.handleChange}
 disabled={busy || !phone.trim()}
 />
 <SmsConsentLabelText />
 </label>

 <label className="lp-field" htmlFor="lp-interest">
 <span>Interested in</span>
 <select {...this.fieldProps('interest')} required>
 <option value="">Select an option</option>
 {INTEREST_OPTIONS.map((option) => (
 <option key={option} value={option}>{option}</option>
 ))}
 </select>
 {this.renderError('interest')}
 </label>
 <label className="lp-field" htmlFor="lp-valueBandId">
 <span>Engagement size</span>
 <select
 {...this.fieldProps('valueBandId')}
 disabled={busy || !interest}
 aria-describedby={errors.valueBandId ? 'lp-valueBandId-error' : 'lp-valueBandId-hint'}
 required
 >
 <option value="">{interest ? 'Select an option' : 'Select an interest first'}</option>
 {valueBands.map((band) => (
 <option key={band.id} value={band.id}>{band.label}</option>
 ))}
 </select>
 {this.renderError('valueBandId') || (
 <small id="lp-valueBandId-hint">
 Helps us prepare. Options update with your interest; no pricing is shown.
 </small>
 )}
 </label>

 <label className="lp-field lp-span-2" htmlFor="lp-message">
 <span>How can we help?</span>
 <textarea
 {...this.fieldProps('message')}
 rows="3"
 placeholder="Renewal coming up in Q2, board asking about DR…"
 />
 </label>
 </div>

 <div className="lp-hp" aria-hidden="true">
 <label htmlFor="lp-fax">Fax</label>
 <input
 id="lp-fax"
 name="fax_number_hp"
 tabIndex={-1}
 autoComplete="off"
 value={this.state.fax_number_hp}
 onChange={this.handleChange}
 />
 </div>

      <TurnstileWidget
        siteKey={TURNSTILE_SITE_KEY}
        action="contact"
        theme="light"
        size="flexible"
        resetSignal={this.state.turnstileReset}
        onVerify={(token) => this.setState({ turnstileToken: token })}
        onExpire={() => this.setState({ turnstileToken: '' })}
        onError={this.handleTurnstileError}
      />

 <button
 className={`lp-btn lp-btn-primary lp-btn-block${busy ? ' is-loading' : ''}`}
 type="submit"
 disabled={busy}
 >
 {busy ? <span className="lp-spinner" aria-hidden="true" /> : null}
 {busy ? 'Sending…' : 'Book a Strategy Review'}
 {busy ? null : <span aria-hidden="true">→</span>}
 </button>
 <p className="lp-form-meta">30-45 min · No system access needed · Clear next steps</p>
 </form>
 );
 }
}

LandingForm.defaultProps = {
 fitSignals: []
};

export default LandingForm;
